/**
 * A pull request page's untouched chat, handed from the page as it unloads to the page's next load
 * in the same tab, so a reload goes on with that chat: the chat's id names the session warming for
 * its first message (`useWarmPageChatSession` hands it over), so the reloaded page keeps that
 * session warming instead of booting another sandbox for a new chat (`PrV2ClientStore` takes it).
 * The page hands its chat over only as it unloads, and the next load takes it, so a page still
 * open never shares its chat, even with a duplicate of its tab, which copies the tab's
 * sessionStorage. Customers' pages never warm a session, so they never hand a chat over.
 */
import { z } from 'zod';

import { WARM_SESSION_TTL_MS } from '@/lib/code-sessions/warm-session-ttl';
import { safeSessionStorage } from '@/lib/safe-storage';
import { STORAGE_KEYS } from '@/lib/storage-keys';

const pageChatHandoffSchema = z.object({
  chatId: z.guid(),
  /** When the page last asked to keep the chat's session warm, by this browser's clock. */
  warmedAt: z.number(),
});

type PageChatHandoff = z.infer<typeof pageChatHandoffSchema>;

/** Hands the page's chat to the page's next load in this tab. */
export function handOverPageChat(pullRequestId: string, handoff: PageChatHandoff): void {
  safeSessionStorage.setItem(
    STORAGE_KEYS.PR_PAGE_CHAT_HANDOFF(pullRequestId),
    JSON.stringify(handoff),
  );
}

/**
 * The chat the pull request page's last load in this tab handed over, while the session warming
 * for it lasts (`WARM_SESSION_TTL_MS` from its last warm), else null. Taking it removes it, so only
 * one load goes on with the chat.
 */
export function takeHandedOverPageChat(pullRequestId: string): string | null {
  const key = STORAGE_KEYS.PR_PAGE_CHAT_HANDOFF(pullRequestId);
  const stored = safeSessionStorage.getItem(key);
  if (stored === null) {
    return null;
  }
  safeSessionStorage.removeItem(key);
  const handoff = readHandoff(stored);
  return handoff !== null && Date.now() < handoff.warmedAt + WARM_SESSION_TTL_MS
    ? handoff.chatId
    : null;
}

function readHandoff(stored: string): PageChatHandoff | null {
  try {
    const handoff = pageChatHandoffSchema.safeParse(JSON.parse(stored));
    return handoff.success ? handoff.data : null;
  } catch {
    // A handoff this tab cannot read starts a new chat.
    return null;
  }
}
